import { TrackJS } from 'trackjs';
import noop from 'lodash/noop';

import { uploadFile } from '../../transfer-bundle/services/multipart-uploader/upload-file';
import { ChunksQueue } from '../../transfer-bundle/services/queues/chunks-queue';
import { TransferAPI } from '../../transfer-bundle/services/api/transfer';
import { processChunkTask } from '../../transfer-bundle/services/multipart-uploader/upload-chunk';

import FileEntry from './file-entry';

import Progress from './progress';

import Config from './config';

import Queue from './queue';

import Gauge from './gauge';

import API from './api';

import {
  FREE_EXPIRY,
  FREE_EXPIRY_ON_CHANNEL,
  MAX_MESSAGE_SIZE_IN_BYTES,
  REGEXP_EMAIL,
  REGEXP_BLANK,
} from './config';

import Logger from '@module/logger';
import LaunchDarkly from '@module/launchdarkly';
import { FF_REFACTOR_TRANSFER_MODULE } from '@constant/experiments';

import TransferError from '@app/helper/error';
import TransferErrorMessages from '@app/helper/error/messages';
import * as TransferItemsHelper from '@app/helper/transfer-items';

import Build, {
  normalizeTransfer,
  convertExpiry,
} from '@module/transfer/build';
import EventEmitter from '@module/wt-event-emitter';

/*
 * plus == never
 * free && onChannel == 4 weeks
 * free && not on channel == 1 week
 */
const determineExpiry = (userType, onChannel) => {
  if (userType === 'plus') {
    return 'never';
  }

  if (onChannel) {
    return FREE_EXPIRY_ON_CHANNEL;
  }

  return FREE_EXPIRY;
};

let chunksQueue = null;

export default class Transfer extends EventEmitter {
  static pauseUpload() {
    if (chunksQueue) {
      chunksQueue.pause();
    }
  }

  static resumeUpload() {
    if (chunksQueue) {
      chunksQueue.resume();
    }
  }

  // Static method that loads transfer details based on their public id and secret.
  static async load(transferId, secret, recipientId = false, password = false) {
    if (!transferId || !secret) {
      throw 'InvalidDownload';
    }
    return await API.fetchTransfer(transferId, secret, recipientId, password);
  }

  static configure(options) {
    if (options.api) {
      // This will update the API options, if any
      API.configure(options.api);
    }
  }

  // Constructor for the Transfer object
  constructor({
    userType = 'free',
    type = 'email',
    onChannel = false,
    accountId,
  } = {}) {
    super();

    // This resets the counter for iOS filenames
    FileEntry.resetFilenameCounter();

    this.id = false; // the public id
    this.parent_id = false; // the parent id (for forwarding)
    this.state = 'init'; // init, started, paused, aborted, finished[, downloadable, downloading]
    this.shortURL = null; // the short (or long) url

    this.setUserType(userType);
    this.type = type === 'link' ? 'link' : 'email'; // The type decides whether it is a `link` or `email` transfer.

    this.language = 'en'; // What language is this?
    this.files = []; // Contains FileEntry objects
    this.items = [];
    this.itemsTree = TransferItemsHelper.buildItemsTree();
    this.sender = ''; // Contains e-mail address
    this.message = ''; // Contains transfer message
    this.channel = null; // Contains channel name
    this.password = null; // Contains a transfer password
    this.recipients = []; // Contains recipients (e-mail addresses)
    this.max_transfer_size = null;
    this.domain_user_id = null;

    if (accountId) {
      this.accountId = accountId;
    }

    // This object contains progress information about the Transfer
    this.progress = {
      duration: false,
      percentage: false,
      remaining: false,
      total: false,
      sent: false,
    };

    // Determine expiry based on user type and if it is on the channel, or, not.
    this.expiry = determineExpiry(this.userType, onChannel);

    /* @private */ this._gauge = new Gauge(this);
  }

  static buildFromParams(json = {}, password = false) {
    const transfer = new Transfer();

    json = normalizeTransfer(json);
    if (json.items) {
      transfer.items = json.items;
    }
    transfer.itemsTree = json.itemsTree;
    transfer.title = json.title;

    if (json.id) {
      transfer.id = json.id;
    }
    if (json.shortened_url) {
      transfer.shortURL = json.shortened_url;
    }
    if (json.security_hash) {
      transfer.secret = json.security_hash;
    }
    if (json.recipient_id) {
      transfer.recipient_id = json.recipient_id;
    }
    if (json.state) {
      transfer.state = json.state;
    }
    if (json.message) {
      transfer.message = json.message;
    }
    if (json.account_id) {
      transfer.accountId = json.account_id;
    }

    if ('description' in json) {
      transfer.message = json.description;
    }
    if ('expiry_in_seconds' in json) {
      transfer.expiry_seconds = json.expiry_in_seconds;
    }
    if ('version' in json) {
      transfer.version = json.version;
    }
    if ('versioning' in json) {
      transfer.versioning = json.versioning;
    }

    if (transfer.isProcessing) {
      transfer.state = 'processing';
    }

    // If we are password protected and don't know the password, change the state
    if (
      (transfer.state === 'processing' || transfer.state === 'downloadable') &&
      json.password_protected === true &&
      !password
    ) {
      transfer.state = 'password_protected';
    }

    // If we are password protected and have the password, remember if for later.
    if (json.password_protected && password) {
      transfer.password = password;
    }

    if ('show_feedback' in json) {
      transfer.showFeedback = json.show_feedback;
    }

    return transfer;
  }

  setUserType(userType, expiry = false) {
    this.userType = userType;

    // We'll need to reset some things for free users.
    if (this.userType !== 'plus') {
      this.expiry = FREE_EXPIRY;
    } else if (expiry !== false) {
      this.expiry = expiry;
    }
  }

  add(files) {
    // Are we still on time to add files?
    if (this.hasStarted) {
      throw 'TransferStarted';
    }

    // Make sure we have received 1 or more files
    if (!files) {
      throw 'MissingFiles';
    }

    // Make sure the files input is an Array/FileList/etc.
    // Array.isArray would return false for FileList
    if (typeof files.length === 'undefined') {
      files = [files];
    }

    let withDuplicates = false;
    const validatedFiles = Array.prototype.reduce.call(
      files,
      (result, file) => {
        const fileEntry = new FileEntry(file);

        const topLevelItemName = TransferItemsHelper.getSubNodesFromItemName(
          // Relative path only exists if item was dropped into the window.
          // In the case when it was selected through the input control,
          // it will only have `name` property.
          file.relativePath || file.name
        ).shift();
        if (
          this.itemsTree.items.find((item) => item.name === topLevelItemName)
        ) {
          withDuplicates = true;
          return result;
        }

        return [...result, fileEntry];
      },
      []
    );

    // Calculate bytes to be added
    if (!this.canFilesBeAdded(validatedFiles)) {
      throw 'TooManyBytes';
    }

    // Calculate amount of files allowed
    const totalFileCount = validatedFiles.length + this.files.length;
    if (totalFileCount > Config.limits[this.userType].files) {
      throw 'TooManyFiles';
    }

    // Add to the current list of file entries
    Array.prototype.push.apply(this.files, validatedFiles);

    // Only now throw error because we chose to allow non-duplicate filenames
    // to still be added to the list, instead of just blocking everything on error
    if (withDuplicates) {
      throw 'DuplicateFilename';
    }
  }

  remove(fileName) {
    this.files = this.files.filter((file) => file.name !== fileName);
  }

  removeDirectory(directoryName) {
    const path = `${directoryName}/`;
    this.files = this.files.filter(
      (file) => file.name !== directoryName && !file.name.startsWith(path)
    );
  }

  pause() {
    if (this.state !== 'started') {
      throw 'TransferNotStarted';
    }
    this.state = 'paused';

    Transfer.pauseUpload();

    // bugfix https://github.com/WeTransfer/frontend/pull/6157
    // emit dispatches actions from the reducers
    // this generates an error and the users can't resume the transfer
    // wrapping it in a setTimeout temporarily fixes it
    // TODO: refactor the way transfer actions are dispatched
    setTimeout(() => {
      this.emit('pause');
      Progress.update(this);
      Progress.stop(this);
    }, 0);
  }

  resume() {
    if (this.state !== 'paused') {
      throw 'TransferNotStarted';
    }
    this.state = 'started';

    Transfer.resumeUpload();
    if (this._gauge) {
      this._gauge.resume();
    }

    // bugfix https://github.com/WeTransfer/frontend/pull/6157
    // emit dispatches actions from the reducers
    // this generates an error and the users can't resume the transfer
    // wrapping it in a setTimeout temporarily fixes it
    // TODO: refactor the way transfer actions are dispatched
    setTimeout(() => {
      this.emit('resume');
      Progress.update(this);
      Progress.start(this);
    }, 0);
  }

  reset() {
    // Abort queue
    if (chunksQueue) {
      chunksQueue.abort(true);
    }
    chunksQueue = false;

    // Abort in API
    if (this.id) {
      API.cancelTransfer(this.id);
    }

    // Abort running API requests
    API.abort();

    // Reset transfer id
    this.id = false;

    // Reset recorded history
    if (this._gauge) {
      this._gauge.reset();
    }

    // Cleanup files
    this.files.forEach((file) => file.reset());

    // Stop progress indicator
    Progress.stop(this);

    // Set state
    this.state = 'init';
  }

  cancel() {
    if (this.state !== 'started' && this.state !== 'paused') {
      throw 'TransferNotStarted';
    }
    this.reset();
  }

  abort(browserExit = false) {
    // If the browserExit flag is set to true, don't actually
    // abort or cancel the current transfer. Trust us, we're on the internet.
    if (browserExit) {
      if (this.id && (this.state === 'started' || this.state === 'paused')) {
        API.exitTransfer(this.id);
      }
      return;
    }

    // Abort queue
    // NOTE: This is also done in .cancel() however there it
    // is done silently. This will shout under the right circumstances.
    if (chunksQueue) {
      chunksQueue.abort();
    }
    chunksQueue = false;

    // Cancel the transfer
    this.cancel();

    // Set state
    this.state = 'aborted';

    // Send our event
    this.emit('abort');
  }

  get isTransferTooBig() {
    return !this.isTransferSizeUnlimited && this.totalSize > this.allowedSize;
  }

  canFilesBeAdded(files) {
    const totalFilesSize = files.reduce((count, file) => file.size + count, 0);
    return (
      this.isTransferSizeUnlimited ||
      this.totalSize + totalFilesSize < this.allowedSize
    );
  }

  get isTransferSizeUnlimited() {
    return this.allowedSize === -1;
  }

  get isForward() {
    return !!this.parent_id;
  }

  get totalSize() {
    return this.items.reduce((totalSize, { size }) => {
      const fileSize = Number(size);
      if (!isNaN(fileSize)) {
        return totalSize + fileSize;
      }
      return totalSize;
    }, 0);
  }

  get availableSize() {
    return this.allowedSize - this.totalSize;
  }

  set allowedSize(size) {
    if (!size || isNaN(size)) {
      this.max_transfer_size = Config.limits[this.userType].bytes;
    }
    this.max_transfer_size = size;
  }

  get allowedSize() {
    return this.max_transfer_size || Config.limits[this.userType].bytes;
  }

  get allowedRecipients() {
    return Config.limits[this.userType].recipient;
  }

  get allowedFiles() {
    return Config.limits[this.userType].files;
  }

  async validateRecaptcha(token) {
    try {
      if (!this.id) {
        throw new TransferError('RecaptchaFailed');
      }
      this.state = 'init';
      await API.validateRecaptcha(this.id, {
        token,
        expire_in: convertExpiry(this.expiry),
      });
    } catch (e) {
      this.state = 'recaptcha';
      throw e;
    }
    return this;
  }

  validate() {
    // Make sure this Transfer only starts once
    if (this.hasStarted) {
      throw new TransferError('AlreadyStarted');
    }
    if (this.type !== 'email' && this.type !== 'link') {
      throw new TransferError('InvalidTransferType');
    }

    if (!this.files.length) {
      throw new TransferError('MissingFiles');
    }

    // For e-mail transfers also make sure we have recipients and sender
    if (this.type === 'email') {
      if (!this.recipients.length) {
        throw new TransferError('MissingRecipients');
      }
      if (!this.sender.replace(REGEXP_BLANK, '')) {
        throw new TransferError('MissingSender');
      }
      if (!this.sender.match(REGEXP_EMAIL)) {
        throw new TransferError('InvalidSender');
      }

      // Make sure the recipients are valid too.
      const invalidRecipients = this.recipients.filter(
        (email) => !email.match(REGEXP_EMAIL)
      );
      if (invalidRecipients.length) {
        throw new TransferError('InvalidRecipient');
      }

      // We only allow so many recipients for a transfer
      if (this.recipients.length > Config.limits[this.userType].recipient) {
        throw new TransferError(TransferErrorMessages.TOO_MANY_RECIPIENTS);
      }
    }

    // For messages we have a max. size (it's pretty high though')
    const messageSize = unescape(encodeURIComponent(this.message)).length;
    if (messageSize > MAX_MESSAGE_SIZE_IN_BYTES) {
      throw new TransferError('MessageTooLarge');
    }

    // If the total transfer size is higher than our limit, notify the user
    if (this.isTransferTooBig) {
      throw new TransferError('TransferTooBig');
    }

    // Does it have more files than we allow?
    if (this.files.length > Config.limits[this.userType].files) {
      throw new TransferError('TooManyFiles');
    }
  }

  async start() {
    this.validate(); // will Throw on errors

    // Create a new Transfer
    try {
      const transferDetails = await API.fetchTransferId(this.type, Build(this));
      if (this.isForward) {
        if (!transferDetails || !transferDetails.id) {
          throw new TransferError('MissingTransferId');
        }
        this.expiry_seconds = transferDetails.expiry_in_seconds;
        this.shortURL = transferDetails.shortened_url;
      }

      this.id = transferDetails.id;

      if (!this.id) {
        throw new TransferError('InvalidTransferId');
      }

      this.verification_required =
        transferDetails.message === 'email_verification_required';
    } catch (e) {
      // FIXME: Temporary check if error is string because request method in transfer/api is not updated yet with custom errors
      if (e === 'RequestAborted') {
        throw new TransferError('TransferCanceled');
      }

      this.state = 'aborted';
      this.emit('error', e);

      this.trackTransferError(e);

      const isErrorObject = typeof e === 'object';
      const isSpecificError =
        (isErrorObject &&
          [
            'LimitReached',
            'UserBlocked',
            'UnavailableParent',
            'HumanVerificationRequired',
          ].indexOf(e.message) !== -1) ||
        [TransferErrorMessages.TOO_MANY_RECIPIENTS].indexOf(e);

      if (isSpecificError) {
        throw e;
      }

      throw new TransferError('TransferFailed', { transferId: this.id });
    }

    // Return the details
    return this;
  }

  async upload() {
    let transferDetails;

    // Reset the progress counters
    this.progress = {
      duration: 0,
      percentage: 0,
      remaining: false,
      total: this.totalSize,
      sent: 0,
    };

    this.state = 'started';
    this.emit('start');

    this._gauge.measure();
    Progress.start(this);

    // If it is not a forward
    if (!this.isForward) {
      try {
        if (LaunchDarkly.getVariant(FF_REFACTOR_TRANSFER_MODULE)) {
          TrackJS.addMetadata(FF_REFACTOR_TRANSFER_MODULE, true);
          await this.uploadFiles();
        } else {
          await this.legacyUploadFiles();
        }
      } catch (error) {
        Logger.error(error);
        this.trackTransferError(error);

        if (error === 'TransferAbortedSilent') {
          throw 'TransferCanceled';
        }

        if (chunksQueue) {
          chunksQueue.abort();
        }
        chunksQueue = false;

        Progress.stop(this);
        this.state = 'aborted';

        this.emit('error', error);
        throw 'TransferFailed';
      }

      // Finalize the transfer and return short url or smth.
      try {
        if (!this.id) {
          throw 'MissingTransferId';
        }
        transferDetails = await API.finalizeTransfer(this.id);
      } catch (error) {
        this.trackTransferError(error);

        this.state = 'aborted';
        this.emit('error', error);
        throw 'TransferFailed';
      }

      // NOTE: This logic also exists somewhere else in the API, maybe it should
      // be centralized to just 1 location at some point in time.
      this.expiry_seconds = transferDetails.expiry_in_seconds;
      this.shortURL = transferDetails.shortened_url;
    }

    // Set the state to finished
    this.state = 'finished';
    this.emit('finish', transferDetails);

    // Force update with 'finished' state (=100%)
    Progress.update(this);
    Progress.stop(this);

    return this;
  }

  async uploadFiles() {
    chunksQueue = new ChunksQueue({ processChunkTask });

    for (const file of this.files) {
      file.id = await TransferAPI.fetchFileObjectId(this.id, {
        name: file.name,
        size: file.size,
      });

      await uploadFile(this.id, file, chunksQueue);
    }
  }

  async legacyUploadFiles() {
    // Create a new queue for our upload job
    chunksQueue = new Queue(this);
    await chunksQueue.start();
  }

  async preview(secret, recipientId = false, fileEntryId = false, geometry) {
    return await API.requestPreview(
      this.id,
      secret,
      recipientId,
      fileEntryId,
      this.password,
      geometry
    );
  }

  // Get the download for this transfer
  async download(
    secret,
    recipientId = false,
    file_ids = false,
    domain_user_id = false
  ) {
    if (!this.id) {
      throw 'InvalidTransfer';
    }
    if (
      this.state !== 'downloadable' &&
      this.state !== 'downloading' &&
      this.state !== 'finished'
    ) {
      throw 'TransferNotDownloadable';
    }
    if (!secret) {
      throw 'MissingSecret';
    }
    if (!file_ids) {
      return await API.fetchTransferDownload(
        this.id,
        secret,
        recipientId,
        false,
        this.password,
        domain_user_id
      );
    }

    if (
      file_ids &&
      !file_ids.every((val) => this.items.find(({ id }) => id === val))
    ) {
      throw 'InvalidTransfer';
    }

    return await API.fetchTransferDownload(
      this.id,
      secret,
      recipientId,
      file_ids,
      this.password,
      domain_user_id
    );
  }

  // A way to verify the password
  async verifyPassword(secret, password, recipientId = false) {
    if (!this.id || !secret) {
      throw 'InvalidDownload';
    }
    if (!password) {
      throw 'EmptyPassword';
    }

    try {
      return await API.fetchTransfer(
        this.id,
        secret,
        recipientId,
        password,
        false
      );
    } catch (e) {
      return false;
    }
  }

  trackTransferError(error) {
    TrackJS.addMetadata('transfer_id', this.id);
    TrackJS.track(error);
  }

  verifyToken(token) {
    return API.verifyToken(this.id, {
      code: token,
      expire_in: convertExpiry(this.expiry),
    });
  }

  resendToken(transferId, senderEmail) {
    return (
      API.resendToken(transferId, senderEmail)
        // end point can return 404, but we don't handle it for now...
        .catch(noop)
    );
  }

  get isSpaceship() {
    return this.id && this.id.charAt(0) === 's';
  }

  get isProcessing() {
    if (this.versioning) {
      return !this.items.length && this.versioning.processing === true;
    }
    return this.state === 'processing';
  }

  get hasStarted() {
    return this.state !== 'init' || this.id;
  }
}
